import React, { useEffect, useState, useRef, useLayoutEffect } from "react";
import { Zap, ChevronRight } from "lucide-react";
import { progressService } from "../../../../../services/progressService.js";
import starsCollection from "../../../../../assets/starsCollection.png";

const FinalSummary = ({ userId, subsectionId, onComplete, onNextStep }) => {
  const service = progressService;

  const [tasks, setTasks] = useState([]);
  const loadedRef = useRef({ userId: null, subsectionId: null, done: false });

  // refs + state for height syncing
  const rightTopRef = useRef(null);
  const rightBottomRef = useRef(null);
  const [rightCombinedHeight, setRightCombinedHeight] = useState(460); // fallback px

  useEffect(() => {
    let mounted = true;

    if (!userId || !subsectionId) return;

    if (
      loadedRef.current.done &&
      loadedRef.current.userId === userId &&
      loadedRef.current.subsectionId === subsectionId
    ) {
      return;
    }

    (async () => {
      if (!service || typeof service.getUserProgress !== "function") {
        console.warn("progressService.getUserProgress not available.");
        return;
      }

      try {
        const saved = await service.getUserProgress(userId, subsectionId);
        const savedTasks = saved?.data?.tasks ?? saved?.tasks ?? null;
        if (!mounted) return;

        if (Array.isArray(savedTasks)) {
          const normalized = savedTasks.map((t, idx) => ({
            id: t.id ?? idx + 1,
            name: t.name ?? `Task ${idx + 1}`,
            energy:
              typeof t.energy === "number"
                ? t.energy
                : t.energy
                ? Number(t.energy)
                : null,
            category: t.category ?? null,
            delegationQuadrant: t.delegationQuadrant ?? null,
            delegateTo: t.delegateTo ?? t.assignedTo ?? "",
            delegationConstraint: t.delegationConstraint ?? t.constraint ?? "",
            delegationOrder: Number.isFinite(t.delegationOrder) ? t.delegationOrder : null,
            subtasks: (t.subtasks || []).map((s, sidx) => ({
              id: s.id ?? `${Date.now()}-${sidx}`,
              name: s.name ?? `Subtask ${sidx + 1}`,
              energy:
                typeof s.energy === "number"
                  ? s.energy
                  : s.energy
                  ? Number(s.energy)
                  : null,
              category: s.category ?? null,
              delegationQuadrant: s.delegationQuadrant ?? null,
              delegateTo: s.delegateTo ?? s.assignedTo ?? "",
              delegationConstraint: s.delegationConstraint ?? s.constraint ?? "",
              delegationOrder: Number.isFinite(s.delegationOrder) ? s.delegationOrder : null,
            })),
          }));

          setTasks(normalized);
          loadedRef.current = { userId, subsectionId, done: true };
        }
      } catch (err) {
        console.warn("Error loading tasks from progressService:", err);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [userId, subsectionId, service]);

  // Collect tasks by quadrant (including subtasks)
  const collect = (quadrant) => {
    const items = [];
    tasks.forEach((t) => {
      if (t.delegationQuadrant === quadrant) items.push(t);
      (t.subtasks || []).forEach((s) => {
        if (s.delegationQuadrant === quadrant) items.push({ ...s, isSubtask: true });
      });
    });
    return items;
  };

  const focusTasks = collect("focus_yourself");
  const delegateTasks = collect("delegate_monitor");
  const coachTasks = collect("coach_team");
  const forgetTasks = collect("delegate_forget");

  const sumEnergy = (list) =>
    list.reduce((acc, it) => acc + (typeof it.energy === "number" ? it.energy : 0), 0);

  const getCategoryBadge = (category) => {
    if (!category) return null;
    const badgeConfig = {
      leverage: { text: "L", bg: "bg-[#DFF3F1]", border: "border-[#54B0AF]" },
      neutral: { text: "N", bg: "bg-[#DFF3F1]", border: "border-[#54B0AF]" },
      overhead: { text: "O", bg: "bg-[#DFF3F1]", border: "border-[#54B0AF]" },
    };
    const config = badgeConfig[category];
    if (!config) return null;

    return (
      <span
        className={`${config.bg} ${config.border} border rounded-full px-2 py-1 text-[10px] font-gotham font-medium text-[#000000] flex items-center gap-2`}
      >
        <span className="flex flex-col gap-[1px]">
          <span className="w-[2px] h-[2px] rounded-full bg-[#54B0AF]" />
          <span className="w-[2px] h-[2px] rounded-full bg-[#54B0AF]" />
          <span className="w-[2px] h-[2px] rounded-full bg-[#54B0AF]" />
        </span>
        {config.text}
      </span>
    );
  };

  const TaskRow = ({ task }) => (
    <div className="flex items-center justify-between gap-3 px-4 py-3 rounded-[9.82px] bg-white border border-[#E6E6E6] shadow-sm">
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <div className="flex gap-[6px] items-center">
          <div className="flex flex-col gap-[3px] opacity-60">
            <span className="w-[3px] h-[3px] rounded-full bg-[#A3A3A3]" />
            <span className="w-[3px] h-[3px] rounded-full bg-[#A3A3A3]" />
            <span className="w-[3px] h-[3px] rounded-full bg-[#A3A3A3]" />
          </div>
          <div className="flex flex-col gap-[3px] opacity-60">
            <span className="w-[3px] h-[3px] rounded-full bg-[#A3A3A3]" />
            <span className="w-[3px] h-[3px] rounded-full bg-[#A3A3A3]" />
            <span className="w-[3px] h-[3px] rounded-full bg-[#A3A3A3]" />
          </div>
        </div>

        <div className="text-[14px] font-gotham font-medium text-[#111827] truncate">
          {task.name}
        </div>
      </div>

      <div className="flex items-center gap-2">
        <div className="bg-[#FAFAFA] border border-[#EEF7F6] rounded-full px-3 py-1 flex items-center gap-2">
          <span className="text-[12px] text-[#111827] font-medium font-gotham">
            {task.energy ?? "-"}%
          </span>
          <Zap className="w-3 h-3 text-[#54B0AF]" stroke="none" fill="#54B0AF" />
        </div>
        {getCategoryBadge(task.category)}
      </div>
    </div>
  );

  // ROI constants from your design
  const focusROI = "10x ROI";
  const delegateROI = "1x ROI";

  // reduce left/middle a bit compared to right combined height
  const reduceAmount = 100;
  const adjustedHeight = Math.max(rightCombinedHeight - reduceAmount, 200);

  // Measure combined height of rightTopRef + rightBottomRef robustly
  // useLayoutEffect(() => {
  //   if (typeof window === "undefined") return;

  //   const gap = 24; // gap between the two stacked cards in px (match your layout gap)
  //   let roTop = null;
  //   let roBottom = null;

  //   const calculate = () => {
  //     const topEl = rightTopRef.current;
  //     const bottomEl = rightBottomRef.current;
  //     if (!topEl || !bottomEl) return;

  //     // use bounding rect heights
  //     const topRect = topEl.getBoundingClientRect();
  //     const bottomRect = bottomEl.getBoundingClientRect();

  //     const combined = Math.round(topRect.height + bottomRect.height + gap);

  //     // set a sensible minimum to avoid tiny heights
  //     const final = combined > 200 ? combined : 200;
  //     setRightCombinedHeight(final);
  //   };

  //   // Use ResizeObserver so changes inside either card update the combined height
  //   try {
  //     if (typeof ResizeObserver !== "undefined") {
  //       roTop = new ResizeObserver(calculate);
  //       roBottom = new ResizeObserver(calculate);

  //       if (rightTopRef.current) roTop.observe(rightTopRef.current);
  //       if (rightBottomRef.current) roBottom.observe(rightBottomRef.current);
  //     }
  //   } catch (e) {
  //     // ignore if ResizeObserver not available
  //   }

  //   // Also recalc on window resize and after images/fonts load
  //   const onWin = () => calculate();
  //   window.addEventListener("resize", onWin);

  //   // Some assets (images, fonts) may change layout after load; observe load events
  //   const imgs = Array.from(document.querySelectorAll("img"));
  //   const onImgLoad = () => calculate();
  //   imgs.forEach((img) => {
  //     if (!img.complete) img.addEventListener("load", onImgLoad);
  //   });

  //   // run initial calculation (after layout)
  //   const raf = requestAnimationFrame(() => {
  //     setTimeout(calculate, 0);
  //   });

  //   return () => {
  //     cancelAnimationFrame(raf);
  //     window.removeEventListener("resize", onWin);
  //     imgs.forEach((img) => {
  //       if (!img.complete) img.removeEventListener("load", onImgLoad);
  //     });
  //     try {
  //       if (roTop && rightTopRef.current) roTop.unobserve(rightTopRef.current);
  //       if (roBottom && rightBottomRef.current) roBottom.unobserve(rightBottomRef.current);
  //       if (roTop) roTop.disconnect();
  //       if (roBottom) roBottom.disconnect();
  //     } catch (e) {
  //       // ignore
  //     }
  //   };
  //   // only re-run measurement when tasks change (content likely changed)
  // }, [tasks]);

  useLayoutEffect(() => {
    if (typeof window === "undefined") return;

    const gap = 24;
    let roTop = null;
    let roBottom = null;

    // ✅ CAPTURE REF VALUES ONCE
    const topEl = rightTopRef.current;
    const bottomEl = rightBottomRef.current;

    const calculate = () => {
      if (!topEl || !bottomEl) return;

      const topRect = topEl.getBoundingClientRect();
      const bottomRect = bottomEl.getBoundingClientRect();

      const combined = Math.round(topRect.height + bottomRect.height + gap);
      const final = combined > 200 ? combined : 200;

      setRightCombinedHeight(final);
    };

    try {
      if (typeof ResizeObserver !== "undefined") {
        roTop = new ResizeObserver(calculate);
        roBottom = new ResizeObserver(calculate);

        if (topEl) roTop.observe(topEl);
        if (bottomEl) roBottom.observe(bottomEl);
      }
    } catch (e) {}

    const onWin = () => calculate();
    window.addEventListener("resize", onWin);

    const imgs = Array.from(document.querySelectorAll("img"));
    const onImgLoad = () => calculate();

    imgs.forEach((img) => {
      if (!img.complete) img.addEventListener("load", onImgLoad);
    });

    const raf = requestAnimationFrame(() => {
      setTimeout(calculate, 0);
    });

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", onWin);

      imgs.forEach((img) => {
        if (!img.complete) img.removeEventListener("load", onImgLoad);
      });

      try {
        // ✅ USE CAPTURED VALUES (NOT ref.current)
        if (roTop && topEl) roTop.unobserve(topEl);
        if (roBottom && bottomEl) roBottom.unobserve(bottomEl);
        if (roTop) roTop.disconnect();
        if (roBottom) roBottom.disconnect();
      } catch (e) {}
    };
  }, [tasks]);

  // Handle button click: prefer onNextStep, fallback to onComplete
  const handleNextModuleClick = async () => {
    try {
      await service.saveProgress(userId, subsectionId, { tasks }, "completed");
    } catch (err) {
      console.warn("FinalSummary: failed to mark completed:", err);
    }

    if (onNextStep && typeof onNextStep === "function") {
      onNextStep();
      return;
    }
    if (onComplete && typeof onComplete === "function") {
      onComplete();
      return;
    }
    console.warn("No onNextStep or onComplete callback provided.");
  };

  return (
    <div className="bg-white">
      <div className="max-w-[1400px] mx-auto">
        <div className="text-center my-6">
          <h1 className="text-[30px] 2xl:text-[32px] font-gotham font-medium text-[#323232]">
            In simple terms, here's what you should prioritise:
          </h1>
        </div>

        {/* Grid: left, center, right(two stacked) */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start mx-10">
          {/* Left - Focus (large) */}
          <div className="col-span-1">
            <div className="rounded-[12px] shadow-md overflow-hidden bg-white border border-[#CCCCCC]">
              <div className="bg-[#1B7977] px-6 py-5 text-center relative">
                <img src={starsCollection} alt="Stars" className="w-8 h-8 object-contain absolute top-5 left-5" />
                <div className="flex items-center justify-center gap-3 mb-2">
                  {/* <img src={starsCollection} alt="Stars" className="w-6 h-6 object-contain" /> */}
                  <div className="text-[11px] text-[#82CDCC] uppercase tracking-wider text-center font-gotham font-medium">
                    KEY TASKS THAT YOU SHOULD
                  </div>
                  {/* <img src={starsCollection} alt="Stars" className="w-6 h-6 object-contain rotate-180" /> */}
                </div>
                <h3 className="text-white text-[20px] font-gotham font-medium">
                  Focus deeply yourself
                </h3>
                  <img src={starsCollection} alt="Stars" className="w-8 h-8 object-contain absolute top-5 right-5" />
              </div>

              {/* APPLY adjusted minHeight to main content area */}
              <div
                className="bg-[#F8F8F8] flex flex-col justify-between"
                style={{ minHeight: `${adjustedHeight}px` }}
              >
                <div className="p-4">
                  <div className="rounded-[10px]">
                    <div className="space-y-3">
                      {focusTasks.slice(0, 4).map((t, i) => (
                        <TaskRow key={i} task={t} />
                      ))}
                    </div>
                  </div>
                </div>

                <div className="p-6 text-center mt-6">
                  <div className="text-[#E6E6E6] text-[48px] font-gotham font-bold leading-none">
                    {focusROI}
                  </div>
                  <div className="text-[#E6E6E6] text-[12px] font-gotham uppercase tracking-wider">
                    {sumEnergy(focusTasks)}% ENERGY SPEND
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Middle - Delegate (large) */}
          <div className="col-span-1">
            <div className="rounded-[12px] shadow-md overflow-hidden bg-white border border-[#CCCCCC]">
              <div className="bg-[#1B7977B2] px-6 py-5 text-center relative">
                <img src={starsCollection} alt="Stars" className="w-8 h-8 object-contain absolute top-5 left-5" />
                <div className="flex items-center justify-center gap-3 mb-2">
                  {/* <img src={starsCollection} alt="Stars" className="w-6 h-6 object-contain" /> */}
                  <div className="text-[11px] text-[#82CDCC] uppercase tracking-wider text-center font-gotham font-medium">
                    KEY TASKS THAT YOU SHOULD
                  </div>
                  {/* <img src={starsCollection} alt="Stars" className="w-6 h-6 object-contain rotate-180" /> */}
                <img src={starsCollection} alt="Stars" className="w-8 h-8 object-contain absolute top-5 right-5" />
                </div>
                <h3 className="text-white text-[20px] font-gotham font-medium">
                  Delegate to Your team
                </h3>
              </div>

              {/* APPLY adjusted minHeight to main content area */}
              <div
                className="bg-[#F8F8F8] flex flex-col justify-between"
                style={{ minHeight: `${adjustedHeight}px` }}
              >
                <div className="p-4">
                  <div className="rounded-[10px]">
                    <div className="space-y-3">
                      {delegateTasks.slice(0, 4).map((t, i) => (
                        <TaskRow key={i} task={t} />
                      ))}
                    </div>
                  </div>
                </div>

                <div className="p-6 text-center mt-6">
                  <div className="text-[#E6E6E6] text-[48px] font-gotham font-bold leading-none">
                    {delegateROI}
                  </div>
                  <div className="text-[#E6E6E6] text-[12px] font-gotham uppercase tracking-wider">
                    {sumEnergy(delegateTasks)}% ENERGY SPEND
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right column: two stacked smaller cards */}
          <div className="col-span-1 flex flex-col gap-6">
            {/* Coach (top small) */}
            <div
              ref={rightTopRef}
              className="rounded-[12px] shadow-md overflow-hidden bg-white border border-[#CCCCCC]"
            >
              <div className="bg-[#414141] px-6 py-5 text-center">
                <div className="flex items-center justify-center gap-3 mb-2">
                  {/* <img src={starsCollection} alt="Stars" className="w-6 h-6 object-contain" /> */}
                  <div className="text-[11px] text-[#B4B4B4] uppercase tracking-wider text-center font-gotham font-medium">
                    TASKS YOU SHOULD
                  </div>
                  {/* <img src={starsCollection} alt="Stars" className="w-6 h-6 object-contain rotate-180" /> */}
                </div>
                <h3 className="text-white text-[16px] md:text-[20px] font-gotham font-medium">
                  Do yourself and Coach them to do
                </h3>
              </div>

              <div className="bg-[#F8F8F8] min-h-[200px] p-4">
                <div className="rounded-[10px]">
                  <div className="space-y-3">
                    {coachTasks.slice(0, 3).map((t, i) => (
                      <TaskRow key={i} task={t} />
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Forget (bottom small) */}
            <div
              ref={rightBottomRef}
              className="rounded-[12px] shadow-md overflow-hidden bg-white border border-[#CCCCCC]"
            >
              <div className="bg-[#7C7D7E] px-6 py-5 text-center">
                <div className="flex items-center justify-center gap-3 mb-2">
                  {/* <img src={starsCollection} alt="Stars" className="w-4 h-4 object-contain" /> */}
                  <div className="text-[11px] text-[#F3F3F3] uppercase tracking-wider text-center font-gotham font-medium">
                    TASKS YOU SHOULD
                  </div>
                  {/* <img src={starsCollection} alt="Stars" className="w-6 h-6 object-contain rotate-180" /> */}
                </div>
                <h3 className="text-white text-[16px] md:text-[20px] font-gotham font-medium">
                  Delegate and forget
                </h3>
              </div>

              <div className="bg-[#F8F8F8] min-h-[200px] p-4">
                <div className="rounded-[10px]">
                  <div className="space-y-3">
                    {forgetTasks.slice(0, 4).map((t, i) => (
                      <TaskRow key={i} task={t} />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer CTA */}
        <div className="flex justify-center mt-12">
          <button
            type="button"
            onClick={handleNextModuleClick}
            className="inline-flex items-center gap-4 px-8 py-4 bg-[#54B0AF] text-white rounded-[22px] font-gotham font-medium text-[18px] hover:shadow-xl transition-all"
          >
            Move Next
            <span className="bg-white rounded-full p-2">
              <ChevronRight className="w-5 h-5 text-[#2E8B87]" />
            </span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default FinalSummary;
